import React from 'react';

const ModuleComparison = () => {
  const modules = [
    {
      name: "ExchangeOnlineManagement",
      azureAppReq: false,
      ps5: true,
      ps7: true,
      capabilities: {
        sharePointSites: false,
        m365Groups: true,
        teamsMembers: false,
        m365Members: true,
        teamChannelsWithSP: false,
        powerApps: false,
        powerFlows: false,
        powerBI: false
      },
      notes: "Primary focus on email and group management. Can retrieve M365 Group info and members with Get-UnifiedGroup and Get-UnifiedGroupLinks."
    },
    {
      name: "MicrosoftTeams",
      azureAppReq: false,
      ps5: true,
      ps7: true,
      capabilities: {
        sharePointSites: false,
        m365Groups: true,
        teamsMembers: true,
        m365Members: true,
        teamChannelsWithSP: false,
        powerApps: false,
        powerFlows: false,
        powerBI: false
      },
      notes: "Excellent for Teams management. Retrieves Teams-enabled M365 groups with Get-Team and team members with Get-TeamUser."
    },
    {
      name: "PnP.PowerShell 1.12.0",
      azureAppReq: false,
      ps5: true,
      ps7: true,
      capabilities: {
        sharePointSites: true,
        m365Groups: false,
        teamsMembers: false,
        m365Members: false,
        teamChannelsWithSP: false,
        powerApps: false,
        powerFlows: false,
        powerBI: false
      },
      notes: "Without Azure app registration, limited to SharePoint operations only. Cannot access M365 Groups, Teams, or Power Platform features."
    },
    {
      name: "PnP.PowerShell 3.x.x",
      azureAppReq: true,
      ps5: false,
      ps7: true,
      capabilities: {
        sharePointSites: true,
        m365Groups: true,
        teamsMembers: true,
        m365Members: true,
        teamChannelsWithSP: true,
        powerApps: true,
        powerFlows: true,
        powerBI: false
      },
      notes: "Requires Azure app registration and PowerShell 7.4.6+. Similar capabilities to version 1.12.0."
    },
    {
      name: "Microsoft.Graph",
      azureAppReq: true,
      ps5: true,
      ps7: true,
      capabilities: {
        sharePointSites: true,
        m365Groups: true,
        teamsMembers: true,
        m365Members: true,
        teamChannelsWithSP: true,
        powerApps: true,
        powerFlows: true,
        powerBI: true
      },
      notes: "Most comprehensive API across all Microsoft 365 services. Requires complex setup and permission configuration."
    },
    {
      name: "AzureAD",
      azureAppReq: false,
      ps5: true,
      ps7: false,
      capabilities: {
        sharePointSites: false,
        m365Groups: true,
        teamsMembers: false,
        m365Members: true,
        teamChannelsWithSP: false,
        powerApps: false,
        powerFlows: false,
        powerBI: false
      },
      notes: "Legacy module (retiring March 2025). Basic group membership operations only. Works only in PowerShell 5."
    },
    {
      name: "Microsoft PowerApps",
      azureAppReq: false,
      ps5: true,
      ps7: false,
      capabilities: {
        sharePointSites: false,
        m365Groups: false,
        teamsMembers: false,
        m365Members: false,
        teamChannelsWithSP: false,
        powerApps: true,
        powerFlows: true,
        powerBI: false
      },
      notes: "Focused exclusively on Power Apps and Power Automate flow management. Only works in PowerShell 5."
    },
    {
      name: "MicrosoftPowerBIMgmt",
      azureAppReq: false,
      ps5: true,
      ps7: true,
      capabilities: {
        sharePointSites: false,
        m365Groups: false,
        teamsMembers: false,
        m365Members: false,
        teamChannelsWithSP: false,
        powerApps: false,
        powerFlows: false,
        powerBI: true
      },
      notes: "Specialized for Power BI administration. Works in both PowerShell 5 and 7."
    },
    {
      name: "Microsoft.Online.SharePoint.PowerShell",
      azureAppReq: false,
      ps5: true,
      ps7: true,
      capabilities: {
        sharePointSites: true,
        m365Groups: false,
        teamsMembers: false,
        m365Members: false,
        teamChannelsWithSP: false,
        powerApps: false,
        powerFlows: false,
        powerBI: false
      },
      notes: "Classic SharePoint Online management. Retrieves site collections but lacks Teams and M365 Groups integration."
    }
  ];

  const capabilities = [
    { id: "sharePointSites", name: "SharePoint Sites" },
    { id: "m365Groups", name: "M365 Groups" },
    { id: "teamsMembers", name: "Teams Members" },
    { id: "m365Members", name: "M365 Members" },
    { id: "teamChannelsWithSP", name: "Team Channels w/SP URL" },
    { id: "powerApps", name: "Power Apps" },
    { id: "powerFlows", name: "Power Automate Flows" },
    { id: "powerBI", name: "Power BI Reports" }
  ];

  // Function to get the recommended modules based on specific needs
  const getRecommendations = () => {
    return (
      <div style={{ marginTop: '2rem', padding: '1.5rem', backgroundColor: '#f8fafc', borderRadius: '0.5rem' }}>
        <h3 style={{ fontSize: '1.125rem', fontWeight: 'bold', marginBottom: '0.75rem' }}>Recommended Modules</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '1rem' }}>
          <div style={{ padding: '1rem', border: '1px solid #e2e8f0', borderRadius: '0.5rem', backgroundColor: 'white' }}>
            <h4 style={{ fontWeight: 'bold', color: '#1e40af' }}>SharePoint Administration</h4>
            <p style={{ marginTop: '0.5rem' }}>
              <span style={{ fontWeight: '600', color: '#1e3a8a' }}>PnP.PowerShell 1.12.0</span> without 
              Azure app registration is limited to SharePoint operations. For cross-service 
              capabilities, either enable app registration or use service-specific modules.
            </p>
          </div>
          <div style={{ padding: '1rem', border: '1px solid #e2e8f0', borderRadius: '0.5rem', backgroundColor: 'white' }}>
            <h4 style={{ fontWeight: 'bold', color: '#15803d' }}>Most Comprehensive</h4>
            <p style={{ marginTop: '0.5rem' }}>
              <span style={{ fontWeight: '600', color: '#166534' }}>Microsoft.Graph</span> offers 
              the most complete API across all Microsoft 365 services, but requires 
              Azure app registration and permission setup.
            </p>
          </div>
          <div style={{ padding: '1rem', border: '1px solid #e2e8f0', borderRadius: '0.5rem', backgroundColor: 'white' }}>
            <h4 style={{ fontWeight: 'bold', color: '#7e22ce' }}>PowerShell 7 Only</h4>
            <p style={{ marginTop: '0.5rem' }}>
              <span style={{ fontWeight: '600', color: '#6b21a8' }}>PnP.PowerShell 3.x.x</span> is optimized for 
              PowerShell 7 with enhanced capabilities, but requires both PowerShell 7.4.6+ and Azure app registration.
            </p>
          </div>
          <div style={{ padding: '1rem', border: '1px solid #e2e8f0', borderRadius: '0.5rem', backgroundColor: 'white' }}>
            <h4 style={{ fontWeight: 'bold', color: '#c2410c' }}>Quick Service-Specific Tasks</h4>
            <p style={{ marginTop: '0.5rem' }}>
              Use specialized modules for single-service operations:
              <span style={{ display: 'block', marginTop: '0.25rem' }}>• Teams: <span style={{ fontWeight: '600' }}>MicrosoftTeams</span></span>
              <span style={{ display: 'block' }}>• Groups: <span style={{ fontWeight: '600' }}>ExchangeOnlineManagement</span></span>
              <span style={{ display: 'block' }}>• Power BI: <span style={{ fontWeight: '600' }}>MicrosoftPowerBIMgmt</span></span>
            </p>
          </div>
        </div>
      </div>
    );
  };

  const getScoreBar = (score) => {
    const percentage = (score / capabilities.length) * 100;
    return (
      <div style={{ width: '100%', height: '0.5rem', backgroundColor: '#e5e7eb', borderRadius: '9999px', overflow: 'hidden' }}>
        <div 
          style={{ 
            height: '100%', 
            borderRadius: '9999px', 
            width: `${percentage}%`,
            backgroundColor: percentage > 75 ? '#22c55e' : 
                             percentage > 50 ? '#3b82f6' : 
                             percentage > 25 ? '#eab308' : '#ef4444'
          }}>
        </div>
      </div>
    );
  };

  const countCapabilities = (module) => {
    return Object.values(module.capabilities).filter(val => val).length;
  };

  return (
    <div style={{ fontFamily: 'sans-serif', padding: '1rem', maxWidth: '1200px', margin: '0 auto' }}>
      <h1 style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '1.5rem', textAlign: 'center' }}>Microsoft 365 PowerShell Module Comparison</h1>
      
      <div style={{ marginBottom: '2rem' }}>
        <h2 style={{ fontSize: '1.25rem', fontWeight: 'bold', marginBottom: '1rem' }}>Capability Overview</h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))', gap: '1rem' }}>
          {modules.map((module, idx) => {
            const capabilityCount = countCapabilities(module);
            return (
              <div key={idx} style={{ 
                border: `1px solid ${module.azureAppReq ? '#fcd34d' : '#e5e7eb'}`, 
                borderRadius: '0.5rem', 
                overflow: 'hidden' 
              }}>
                <div style={{ 
                  padding: '1rem', 
                  backgroundColor: module.azureAppReq ? '#fef3c7' : 'white' 
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <h3 style={{ fontWeight: 'bold', fontSize: '1.125rem' }}>{module.name}</h3>
                    <div style={{ display: 'flex' }}>
                      <span style={{ 
                        fontSize: '0.75rem', 
                        fontWeight: 'bold', 
                        padding: '0.25rem 0.5rem', 
                        borderRadius: '0.25rem', 
                        backgroundColor: module.ps5 ? '#dbeafe' : '#f3f4f6', 
                        color: module.ps5 ? '#1e40af' : '#6b7280',
                        marginRight: '0.25rem'
                      }}>
                        PS5
                      </span>
                      <span style={{ 
                        fontSize: '0.75rem', 
                        fontWeight: 'bold', 
                        padding: '0.25rem 0.5rem', 
                        borderRadius: '0.25rem', 
                        backgroundColor: module.ps7 ? '#dcfce7' : '#f3f4f6', 
                        color: module.ps7 ? '#166534' : '#6b7280'
                      }}>
                        PS7
                      </span>
                    </div>
                  </div>
                  
                  {module.azureAppReq && (
                    <div style={{ color: '#b45309', fontSize: '0.875rem', fontWeight: '600', marginTop: '0.25rem' }}>
                      Requires Azure App Registration
                    </div>
                  )}
                  
                  <div style={{ marginTop: '0.75rem' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.25rem' }}>
                      <span style={{ fontSize: '0.875rem', fontWeight: '500' }}>Capabilities</span>
                      <span style={{ fontSize: '0.875rem', fontWeight: 'bold' }}>{capabilityCount}/{capabilities.length}</span>
                    </div>
                    {getScoreBar(capabilityCount)}
                  </div>
                  
                  <div style={{ marginTop: '0.75rem', fontSize: '0.875rem', color: '#4b5563' }}>
                    {module.notes}
                  </div>
                </div>
                
                <div style={{ backgroundColor: '#f9fafb', padding: '1rem', borderTop: '1px solid #e5e7eb' }}>
                  <h4 style={{ fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.5rem' }}>Supported Operations</h4>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
                    {capabilities.map((cap, capIdx) => (
                      <div key={capIdx} style={{ display: 'flex', alignItems: 'center' }}>
                        <span style={{ 
                          width: '1rem', 
                          height: '1rem', 
                          borderRadius: '9999px', 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'center',
                          backgroundColor: module.capabilities[cap.id] ? '#dcfce7' : '#fee2e2',
                          color: module.capabilities[cap.id] ? '#166534' : '#b91c1c'
                        }}>
                          {module.capabilities[cap.id] ? '✓' : '×'}
                        </span>
                        <span style={{ marginLeft: '0.5rem', fontSize: '0.75rem' }}>{cap.name}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      
      {getRecommendations()}

      <div style={{ marginTop: '2rem', fontSize: '0.875rem', color: '#4b5563' }}>
        <h3 style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}>Key Takeaways</h3>
        <ul style={{ listStyleType: 'disc', paddingLeft: '1.25rem', lineHeight: '1.5rem' }}>
          <li>No single module supports everything - choose based on your specific needs</li>
          <li>PnP.PowerShell 1.12.0 without Azure app registration is limited to SharePoint operations</li>
          <li>Microsoft.Graph is the most comprehensive but requires more setup</li>
          <li>For simplicity, specialized modules may be easier for specific tasks</li>
          <li>PowerShell 7 compatibility varies between modules</li>
        </ul>
      </div>
    </div>
  );
};

export default ModuleComparison;